import { Box } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileRegistStatus,
  FileRegistStatusOptionalAttachments,
  FormClassRetire,
  FormKbn,
  RetireFlowReason,
  RetireReason,
} from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import { TkRadioGroup } from "../../components/atoms/TkRadioGroup";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkFileUpload from "../../components/molecules/TkFileUpload";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { TkRouteContext } from "../../context/tkRouteContext";
import { TkFileUploadFileType } from "../../type/componentsType/TkFileUpload";
import "./_style.scss";
import {
  fakeTableList,
  fakeTableListField,
  FormClassRetirePats,
  stepper,
} from "./parts";
import { IAPI_STA008_Request, IAPI_STA037_Response } from "../../type/apiType";
import { httpAPI_STA008, httpAPI_STA037, httpAPI_STA038 } from "./api/request";
import TkPropertyMessageGet from "../../utils/TkPropertyMessageGet";
import { API_STA008_BuildReqParams } from "../../utils/buildRequestParams";
import _ from "lodash";
import { toNumberOrUndefined } from "../../utils/useTools";
import { TkGetCode } from "../../utils/TkGetCode";

/**
 * SCRC060121 退職情報　登録（添付書類の確認）
 * 職員の退職時の他退職手当金の情報を登録する。
 * @return SCRC060121 退職情報　登録（添付書類の確認）
 */
const SCRC060121 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const { state: routeState } = useContext(TkRouteContext);

  const [stateInfoList, setStateInfoList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [stateInfoListField, setStateInfoListField] = useState<any>([]);
  const stateInfoFakeTableListField = useRef(_.cloneDeep(fakeTableListField));

  const [resData, setResData] = useState<IAPI_STA037_Response>({});
  const [isRegistration, setIsRegistration] = useState<boolean>(false);

  // 一時保存ステップ
  const [tempSaveStep, setTempSaveStep] = useState<number>(0);
  // 添付書類登録状態
  const [targetFormClass, setTargetFormClass] = useState<string[]>([]);

  const [fileRegistStatus, setFileRegistStatus] = useState<{
    [key: string]: FileRegistStatusOptionalAttachments | null;
  }>({});
  // 添付書類リスト
  const [fileList, setFileList] = useState<{
    [key: string]: TkFileUploadFileType[] | undefined;
  }>({});
  // ダイアログ開閉状態（合計）
  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState<boolean>(false);

  // ユーザタイプ
  const userType = state?.authInfo?.userType;
  // ユーザタイプが機構
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;

  const routerList = routeState.routerList || [];
  const fromPage =
    routerList.length > 0 ? `${routerList[routerList.length - 1].href}` : "";

  // 当画面扱う帳票種別を定義
  const formClassIllness = ["8", "11", "12"]; // 疾病による退職
  const formClassDeath = ["9", "13", "44", "10"]; // 死亡による退職
  const formClassCrime = ["15", "16", "17", "5", "18", "19", "20", "6"]; // 犯罪等による退職

  const [errorMessages, setErrorMessages] = useState<{
    [key: string]: string;
  }>({});

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInit = async () => {
    if (["/SCRC060031", "/SCRD310002"].includes(fromPage)) {
      try {
        const res = await httpAPI_STA037({
          applyId: location.state?.applyId!,
        });

        if (res.resultCd === "I0000") {
          // 上の表示テーブル
          setFakeTableList(
            businessState.job_32?.staffNo,
            businessState.job_32?.staffNameKanji
          );

          // 下の表示テーブル
          setFakeTableListField(
            businessState.job_32?.nRetireReasonCd,
            businessState.job_32?.retireProcedureReason,
            !!res.formList ? res.formList : [],
            true
          );

          // 帳票リストをステートに保存
          if (!!res.formList) {
            const formList: any[] = res.formList;
            const newFileList = { ...fileList };
            formList.forEach((item) => {
              const data = {
                formName: item.fileName,
                fileBase64: item.fileBase64,
              };
              newFileList[item.formClass] = [data];
            });
            setFileList(newFileList);
          }

          setResData(res);
          setIsRegistration(true);

          setState({
            ...state,
            errorMsgId: "",
          });
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      // 一時保存ステップを設定
      businessState.job_32?.tempSaveStep &&
        setTempSaveStep(
          toNumberOrUndefined(businessState.job_32?.tempSaveStep) ?? 0
        );

      // 帳票リストをステートに保存
      const formList: any[] = [];
      const fileListData: {
        [key: string]: TkFileUploadFileType[] | undefined;
      } = {};
      const fileList = businessState.job_32?.formList;
      if (!!fileList) {
        fileList.forEach((item) => {
          const formListData = {
            serialNo: item.serialNo,
            facilityNo: item.facilityNo,
            staffNo: item.staffNo,
            formKbn: item.formKbn,
            formClass: item.formClass,
            fileName: item?.formName ?? item?.fileName,
            fileBase64: item.fileBase64,
            formStatus: item.attachDocRegStatus,
          };
          fileListData[item.formClass as string] = [
            {
              formName: item?.formName ?? item?.fileName!,
              fileBase64: item.fileBase64!,
            },
          ];
          formList.push(formListData);
        });
      }
      setFileList(fileListData);

      // 上の表示テーブル
      setFakeTableList(
        businessState.job_32?.staffNo,
        businessState.job_32?.staffNameKanji
      );

      // 下の表示テーブル
      setFakeTableListField(
        businessState.job_32?.nRetireReasonCd,
        businessState.job_32?.retireProcedureReason,
        formList,
        false
      );
    }
  };

  const radioPropsList = Object.entries(FileRegistStatus).map(
    ([label, value]) => ({
      label,
      value,
    })
  );

  const radioPropsListOptionalAttachments = Object.entries(
    FileRegistStatusOptionalAttachments
  ).map(([label, value]) => ({
    label,
    value,
  }));

  const setFakeTableList = (
    staffNoValue: string | undefined,
    staffNameKanjiValue: string | undefined
  ): void => {
    let resArr = stateFakeTableList.current;
    // 契約者（法人）名
    let contractorName = resArr.find(
      (item) => item.keyword === "contractorName"
    )!;
    contractorName.showLine = isUserTypeKiko;
    contractorName.text = `${businessState.common?.contractorNo}:${businessState.common?.contractorName}`;
    // 職員番号
    let staffNo = resArr.find((item) => item.keyword === "staffNo")!;
    staffNo.text = staffNoValue;
    // 退職者名
    let retireeName = resArr.find((item) => item.keyword === "retireeName")!;
    retireeName.text = staffNameKanjiValue;

    setStateInfoList([...resArr]);
  };

  const setFakeTableListField = (
    retireReasonCd: string | undefined,
    retireProcedureReason: string | undefined,
    formList: any[],
    registerLater: boolean
  ): void => {
    let resArrReasonFiles = stateInfoFakeTableListField.current;
    // 退職理由
    const retireReasonDisplay = TkGetCode(RetireReason, retireReasonCd);
    const retireProcedureReasonDisplay = TkGetCode(
      RetireFlowReason,
      retireProcedureReason
    );
    let retireReason = resArrReasonFiles.find(
      (item) => item.keyword === "retireReason"
    )!;
    retireReason.text =
      retireReasonCd === "3"
        ? retireReasonDisplay
        : `${retireReasonDisplay}＆` + retireProcedureReasonDisplay;

    // 画面に表示するファイルの編集
    [...formClassIllness, ...formClassDeath, ...formClassCrime].forEach(
      (formClass) => {
        let fileUpload = resArrReasonFiles.find(
          (item) => item.keyword === "uploadFormClass" + formClass
        )!;
        fileUpload.showLine = false;
      }
    );
    const targetFormClass = [];
    if (registerLater) {
      targetFormClass.push(
        ...formList
          .filter((item) => item.formClass !== undefined)
          .map((item) => item.formClass)
      );
    } else {
      if (
        retireReasonCd ===
        RetireReason["業務上の傷病又は業務上の死亡による退職"]
      ) {
        if (retireProcedureReason === RetireFlowReason["傷病による退職"]) {
          targetFormClass.push(...formClassIllness);
        } else if (
          retireProcedureReason === RetireFlowReason["死亡による退職"]
        ) {
          targetFormClass.push(...formClassDeath);
        }
      } else if (retireReasonCd === RetireReason["犯罪等による退職"]) {
        targetFormClass.push(...formClassCrime);
      }
    }
    const initFileRegistStatus = targetFormClass.reduce((acc, curr) => {
      acc[curr] = "";
      return acc;
    }, {} as { [key: string]: string });
    targetFormClass.forEach((formClass) => {
      let fileUpload = resArrReasonFiles.find(
        (item) => item.keyword === "uploadFormClass" + formClass
      )!;
      const fileDisplayName = Object.keys(FormClassRetirePats).find(
        (key) =>
          FormClassRetirePats[key as keyof typeof FormClassRetirePats] ===
          formClass
      ) as keyof typeof FormClassRetirePats | undefined;
      const formItemData: any = formList.find(
        (item) => item.formClass === formClass
      );
      const radioValue = !!formItemData ? formItemData.formStatus : "";
      initFileRegistStatus[formClass] = radioValue;
      fileUpload.title = `添付ファイル（${fileDisplayName}）`;
      fileUpload.showLine = true;
    });
    setFileRegistStatus(initFileRegistStatus);
    setTargetFormClass(targetFormClass);

    setStateInfoListField([...resArrReasonFiles]);
  };

  const handleRadioGetValue = (data: any, formClass: string): void => {
    const value = data?.target?.value;
    setFileRegistStatus({
      ...fileRegistStatus,
      [formClass]: value,
    });
    if (value === FileRegistStatusOptionalAttachments["今すぐ登録"]) {
      let newErrorMessages = errorMessages;
      newErrorMessages[formClass] = TkPropertyMessageGet("TKMSG_SCR0044E");
      setErrorMessages({
        ...newErrorMessages,
      });
    } else {
      const { [formClass]: removedKey, ...rest } = fileList;
      setFileList(rest);
    }
  };

  useEffect(
    () => {
      let resArrReasonFiles = stateInfoFakeTableListField.current;
      Object.keys(fileRegistStatus).forEach((formClass) => {
        let fileUpload = resArrReasonFiles.find(
          (item) => item.keyword === "uploadFormClass" + formClass
        )!;
        const fileData: any = fileList[formClass];
        const fileSetValue =
          !!fileData &&
          Array.isArray(fileData) &&
          fileData.length > 0 &&
          !!fileData[0]?.formName &&
          fileData[0]?.formName.length > 0
            ? [
                {
                  formName: fileData[0]?.formName,
                  fileBase64: fileData[0]?.fileBase64,
                },
              ]
            : [];
        const autoCheck =
          fileData && fileData.length > 0 && !!fileData[0].formName
            ? FileRegistStatusOptionalAttachments["今すぐ登録"]
            : null;
        fileUpload.node = (
          <Box>
            {(formClassCrime.slice(-4).includes(formClass) ||
              formClassDeath.slice(-1).includes(formClass)) && (
              <TkRadioGroup
                radioPropsList={radioPropsListOptionalAttachments}
                onChange={(data) => handleRadioGetValue(data, formClass)}
                wantToSetValue={
                  !fileRegistStatus[formClass]
                    ? autoCheck
                    : fileRegistStatus[formClass]
                }
              />
            )}
            {!(
              formClassCrime.slice(-4).includes(formClass) ||
              formClassDeath.slice(-1).includes(formClass)
            ) && (
              <TkRadioGroup
                radioPropsList={radioPropsList}
                onChange={(data) => handleRadioGetValue(data, formClass)}
                wantToSetValue={
                  !fileRegistStatus[formClass]
                    ? autoCheck
                    : fileRegistStatus[formClass]
                }
              />
            )}
            {fileRegistStatus[formClass] !== "9" &&
              fileRegistStatus[formClass] !== "0" && (
                <TkFileUpload
                  error={!!errorMessages[formClass]}
                  errorMsg={errorMessages[formClass]}
                  data-testid={"fileUpload" + formClass}
                  id={"fileUploadId" + formClass}
                  multiple={false}
                  onChange={(e: any, type?: string) =>
                    handleFileUploadChange(formClass, e, type)
                  }
                  wantToSetValue={fileSetValue}
                />
              )}
          </Box>
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileRegistStatus, JSON.stringify(errorMessages)]
  );

  const handleFileUploadChange = (
    formClass: string,
    e: any,
    type?: string
  ): void => {
    if (type && e?.target?.value) {
      let newFileList = { ...fileList };
      newFileList[formClass] = e.target.value as
        | TkFileUploadFileType[]
        | undefined;
      setFileList(newFileList);
      let newErrorMessages = errorMessages;
      if (type === "remove") {
        newErrorMessages[formClass] = TkPropertyMessageGet("TKMSG_SCR0044E");
      } else {
        newErrorMessages[formClass] = "";
      }
      setErrorMessages({
        ...newErrorMessages,
      });
      return;
    }
  };

  const makeFormList = () => {
    let newArr = (
      businessState.job_32?.formList?.filter(
        (item) =>
          !(
            item.formKbn === FormKbn["退職書類"] &&
            (item.formClass === FormClassRetire["就業規則（写）"] ||
              item.formClass === FormClassRetire["その他機構が求めた書類"] ||
              item.formClass ===
                FormClassRetire["現認証明書（事務取扱要領　様式10-2号）"] ||
              item.formClass ===
                FormClassRetire["現認証明書（事務取扱要領　様式10-3号）"] ||
              item.formClass ===
                FormClassRetire["生計維持に関する調査書（様式11号）"] ||
              item.formClass ===
                FormClassRetire[
                  "障害補償給付又は傷病補償年金の支給決定通知書（写）"
                ] ||
              item.formClass ===
                FormClassRetire[
                  "障害厚生年金の受給を証する書類又は医師の診断書"
                ] ||
              item.formClass === FormClassRetire["死亡診断書（写）"] ||
              item.formClass ===
                FormClassRetire[
                  "葬祭料の支給決定通知書（写）又は第三者行為災害届（写）"
                ] ||
              item.formClass ===
                FormClassRetire["問題行動の概要が分かる書類"] ||
              item.formClass ===
                FormClassRetire["懲戒処分・解雇の辞令（写）"] ||
              item.formClass ===
                FormClassRetire["解雇処分について審議された議事録（写）"] ||
              item.formClass ===
                FormClassRetire[
                  "事情聴取の調書等の記録書面（写）、自認書（写）"
                ] ||
              item.formClass === FormClassRetire["判決書（写）"] ||
              item.formClass === FormClassRetire["新聞記事（写）"])
          )
      ) ?? []
    ).map((item) => {
      const { attachDocRegStatus, formName, ...rest } = item;
      return {
        ...rest,
        fileName: item?.formName ?? item?.fileName,
        formStatus: item.attachDocRegStatus,
        attachDocRegStatus: item.attachDocRegStatus,
      };
    });

    targetFormClass.forEach((formClass) => {
      if (!!fileList[formClass]) {
        const file = fileList[formClass]![0];
        newArr.push({
          staffNo: businessState.job_32?.staffNo,
          formKbn: "6", // 帳票区分
          formClass: formClass, // 帳票種別
          fileName: file?.formName, // ファイル名
          fileBase64: file?.fileBase64,
          formStatus: fileRegistStatus[formClass] as string,
          attachDocRegStatus: fileRegistStatus[formClass] as string,
        });
      } else if (
        fileRegistStatus[formClass] !==
        FileRegistStatusOptionalAttachments["今すぐ登録"]
      ) {
        newArr.push({
          staffNo: businessState.job_32?.staffNo,
          formKbn: "6", // 帳票区分
          formClass: formClass, // 帳票種別
          fileName: "", // ファイル名
          fileBase64: "",
          formStatus: fileRegistStatus[formClass] as string,
          attachDocRegStatus: fileRegistStatus[formClass] as string,
        });
      }
    });
    newArr.forEach((item: any, index: any) => {
      item.serialNo = index + 1;
    });

    return newArr;
  };

  const makeHttpObj = (data: any) => {
    // 1.持回情報.退職理由コード=1以外の場合
    // "JOB-31"を設定
    // 2.持回情報.退職理由コード=1かつ
    // 持回情報.退職手続きの理由=法人都合かつ
    // 持回情報.継続異動該当条件１フラグ=1かつ
    // 持回情報.継続異動該当条件２フラグ=1の場合
    // "JOB-33"を設定
    // 3.上記以外の場合
    // "JOB-32"を設定
    let businessType = "";

    if (businessState.job_32?.nRetireReasonCd === RetireReason["普通退職"]) {
      businessType = "JOB-32";
      businessState.job_32?.retireProcedureReason ===
        RetireFlowReason["法人（業務上の）都合"] &&
        (businessType =
          businessState.job_32?.continueTransferApplicable1Flg === "1" &&
          businessState.job_32?.continueTransferApplicable2Flg === "1"
            ? "JOB-33"
            : "JOB-32");
    } else {
      businessType =
        businessState.job_32?.retireProcedureReason ===
        RetireFlowReason["法人（業務上の）都合"]
          ? "JOB-32"
          : "JOB-31";
    }

    const reqObj: IAPI_STA008_Request = {
      ...data,
      contractorNo: isUserTypeKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo,
      applyId: businessState.job_32?.applyId,
      tempSaveStep: 2,
      businessType,
      formList: makeFormList(),
      screenId: "SCRC060121",
    };
    return API_STA008_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    // バリデーションチェック
    let checkError: boolean = false;
    const updatedErrorMessages = { ...errorMessages };
    if (targetFormClass.length > 0) {
      targetFormClass.forEach((formClass) => {
        updatedErrorMessages[formClass] = "";
        if (!fileRegistStatus[formClass]) {
          // 未選択の場合
          checkError = true;
          updatedErrorMessages[formClass] =
            TkPropertyMessageGet("TKMSG_SCR0044E");
        } else {
          // 「今すぐ登録」でファイルがアップロードしない場合
          if (
            fileRegistStatus[formClass] ===
            FileRegistStatusOptionalAttachments["今すぐ登録"]
          ) {
            const fileData: any = fileList[formClass];
            if (!(Array.isArray(fileData) && fileData.length > 0)) {
              checkError = true;
              updatedErrorMessages[formClass] =
                TkPropertyMessageGet("TKMSG_SCR0044E");
            }
          }
        }
      });
    }
    if (checkError) {
      setErrorMessages(updatedErrorMessages);
      return;
    }

    if (index === 0) {
      // 一時保存
      try {
        const res = await httpAPI_STA008({
          ...makeHttpObj({ ...data, ...businessState.job_32 }),
        });

        if (res.resultCd === "I0000") {
          setBusinessState({
            ...businessState,
            job_32: {
              ...businessState.job_32,
              tempSaveStep: 2,
              applyId: res.applyId,
              updateDateTime: res.updateDateTime,
              formList: makeFormList(),
            },
          });
          setState({
            ...state,
            errorMsgId: "",
          });
          setIsOpenSaveDialog(true);
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      // 次へ
      // 持回り情報を保存;
      setBusinessState({
        ...businessState,
        job_32: {
          ...businessState.job_32,
          formList: makeFormList(),
        },
      });
      setState({
        ...state,
        errorMsgId: "",
      });
      navigate("/SCRC060122");
    }
  };

  // 登録
  const handleRegistration = async () => {
    const updateFormList = makeFormList();
    updateFormList.forEach((item) => {
      delete item.attachDocRegStatus;
    });

    try {
      const res = await httpAPI_STA038({
        applyId: resData.applyId,
        formList: updateFormList,
        screenId: "SCRC060121",
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });

        navigate("/SCRC060031");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRC060121PageForTest" className="SCRC060121">
      {!isRegistration && (
        <>
          <TkStepper steps={stepper} activeStep={1} savaStep={tempSaveStep} />
          <Box className="explain">
            <TkInfoManual rule={"\\n"}>
              {labelids.labelid.LBLC060121_01}
            </TkInfoManual>
          </Box>
        </>
      )}
      <TkFakeTable list={stateInfoList} ruleSchema={{}} showButton={false} />
      <TkFakeTable
        list={stateInfoListField}
        ruleSchema={{}}
        onCheckAndSubmit={(data, index) => {
          if (isRegistration) {
            handleRegistration();
          } else {
            handleSubmit(data, index);
          }
        }}
        buttonGroup={<TkBackButton useBackParam={true} />}
        submitButtonGroup={[
          { submitButtonText: "一時保存", showButton: !isRegistration },
          { submitButtonText: "次へ", showButton: !isRegistration },
          { submitButtonText: "登録", showButton: isRegistration },
        ]}
      />
      <TkMessageDialog
        open={isOpenSaveDialog}
        onOky={() => setIsOpenSaveDialog(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRC060121;
